import * as firebase from "firebase/app";
import * as qs from "qs";
import { IAdminUpdateApplicationStatusFuncParams } from "shared/functions/admin/updateApplication";
import {
  IApiCreateInvoiceFuncParams,
  IApiCreateInvoiceFuncResponse
} from "shared/functions/api/createInvoice";
import { ICanSignInRequest } from "shared/functions/common/ICanSignIn";
import {
  ApiEndpoints,
  FunctionName,
  InternalApiEndpoints,
  SLResponse,
  LeadApiEndpoints
} from "shared/functions/common";
import {
  GetDepositPaymentInfoFuncParams,
  GetDepositPaymentInfoResponse
} from "shared/functions/getDepositPaymentInfo";
import { MemberPaymentInfo } from "shared/functions/memberPayments";
import { MailData } from "shared/emails/MailData";
import { IUpdateApplicationPlaidAuthFuncParams } from "shared/functions/updateApplicationPlaidAuth";
import { IGeneratePlaidAssetReportFuncParams } from "shared/functions/generatePlaidAssetReport";
import { IUpdateMemberVerificationFuncParams } from "shared/functions/updateMemberVerification";
import { IWarmUpFuncParams } from "shared/functions/warmup";
import { ICreateFollowupJobFuncParams } from "shared/functions/createFollowupJob";
import {
  ILinkedinRedirectFuncParams,
  LinkedinRoutes,
  OAuthRoutes
} from "shared/functions/oauth";

import Config from "../../Config/Config";
import { NetworkService } from "../../Network/NetworkService";
import Utils from "../../utils/Utils";

export default class FirebaseFunctionsService {
  // We warm up our internal api every time the dashboard loads.
  // At the moment, there isnt a lot of usage for the dashboard, and when accessing some feature (firestore)
  // There are long cold starts (more than 10s sometimes). Hopefully we'll revisit this soon.
  public static warmupInternalApi = async (
    params: IWarmUpFuncParams = { firestore: true }
  ) => {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.warmup
    );

    return NetworkService.get(url, params);
  };

  public static getDepositPaymentInfo = async (
    params: GetDepositPaymentInfoFuncParams
  ) => {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.getDepositPaymentInfo
    );
    return NetworkService.get<SLResponse<GetDepositPaymentInfoResponse>>(
      url,
      params
    );
  };

  public static canSignIn = async (params: ICanSignInRequest) => {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.canSignIn
    );

    return NetworkService.get(url, params);
  };

  public static memberPayment = async (params: MemberPaymentInfo) => {
    Utils.removeUndefinedKeys(params);
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.memberPayments
    );
    return NetworkService.post(url, params);
  };

  public static sendEmail = async <T = any>(params: MailData<T>) => {
    Utils.removeUndefinedKeys(params);
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.sendEmail
    );
    return NetworkService.post(url, params);
  };

  public static async adminUpdateApplicationStatus(
    params: IAdminUpdateApplicationStatusFuncParams
  ) {
    const response = await firebase
      .functions()
      .httpsCallable(FunctionName.adminUpdateApplicationStatus)(params);
    return response.data;
  }

  public static updateApplicationPlaidAuth = async (
    params: IUpdateApplicationPlaidAuthFuncParams
  ): Promise<SLResponse> => {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.updateApplicationPlaidAuth
    );
    return NetworkService.post(url, params);
  };

  public static generatePlaidAssetReport = async (
    params: IGeneratePlaidAssetReportFuncParams
  ): Promise<SLResponse> => {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.generatePlaidAssetReport
    );
    return NetworkService.post(url, params);
  };

  public static async createInvoice(
    params: IApiCreateInvoiceFuncParams
  ): Promise<SLResponse<IApiCreateInvoiceFuncResponse>> {
    const url = FirebaseFunctionsService.apiFunctionUrl(ApiEndpoints.invoices);

    return NetworkService.post(url, params);
  }

  public static async updateMemberVerification(
    params: IUpdateMemberVerificationFuncParams
  ): Promise<SLResponse<any>> {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.updateMemberVerification
    );
    return NetworkService.post(url, params);
  }

  public static async unsubscribeLeadFromFollowups(
    uid : string
  ): Promise<SLResponse<any>> {
    const url = FirebaseFunctionsService.leadsApiFunctionUrl(
      LeadApiEndpoints.unsubscribeLead
    );
    return NetworkService.post(url, {uid});
  }

  public static async createFollowupJob(
    params: ICreateFollowupJobFuncParams
  ): Promise<SLResponse<any>> {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.createFollowupJob
    );
    return NetworkService.post(url, params);
  }

  public static connectWithLinkedin(props: ILinkedinRedirectFuncParams) {
    const oauthUrl = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.oauth
    );
    const propsStr = qs.stringify(props);
    return `${oauthUrl}/${OAuthRoutes.Linkedin}/${
      LinkedinRoutes.Redirect
    }?${propsStr}`;
  }

  public static async partnerRequest(
    params: any
  ): Promise<SLResponse<any>> {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.logRequest
    );
    return NetworkService.post(url, params);
  }

  public static async brokerRequest(
    params: any
  ): Promise<SLResponse<any>> {
    const url = FirebaseFunctionsService.internalApiFunctionUrl(
      InternalApiEndpoints.logRequest
    );
    return NetworkService.post(url, params);
  }

  private static apiFunctionUrl = (endpoint: ApiEndpoints) => {
    const baseUrl = Config.cloudFunctionsUrl();
    return `${baseUrl}/${FunctionName.api}/${endpoint}`;
  };

  private static internalApiFunctionUrl = (endpoint: InternalApiEndpoints) => {
    const baseUrl = Config.cloudFunctionsUrl();
    return `${baseUrl}/${FunctionName.internalApi}/${endpoint}`;
  };

  private static leadsApiFunctionUrl = (endpoint: LeadApiEndpoints) => {
    const baseUrl = Config.cloudFunctionsUrl();
    return `${baseUrl}/${FunctionName.leadsApi}/${endpoint}`;
  };
}



// WEBPACK FOOTER //
// ./src/services/firebase/functions/FirebaseFunctionsService.ts